import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Link2, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { AddExecutiveDialog } from "../../components/AddExecutiveDialog";
import { ImportExecutivesDialog } from "../../components/ImportExecutivesDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, Card, StatusBadge, EmptyState, Loading, Pager, Field } from "../../components/common";
import { api, errMsg } from "../../lib/api";
import { pkr, fmtDate, PAY_METHODS } from "../../lib/format";

const ACC = { active: { label: "Active", tone: "green" }, suspended: { label: "Suspended", tone: "amber" }, deleted: { label: "Deleted", tone: "red" } };

export default function Executives() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  const [reason, setReason] = useState("");
  const [link, setLink] = useState("");
  const [status, setStatus] = useState("");
  const [del, setDel] = useState(null);
  const [delReason, setDelReason] = useState("");
  const load = () => api.get("/admin/executives", { params: { q, page, status } }).then((r) => setData(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, page, status]); // eslint-disable-line
  const doDelete = async () => {
    try {
      const { data: r } = await api.delete(`/admin/executives/${del.id}`, { data: { reason: delReason } });
      toast.success(r.mode === "permanent" ? `${del.name} was permanently deleted` : `${del.name} was deleted. Their ${r.orders} order(s) and commission history are kept for your records.`);
      setDel(null); setDelReason(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async () => {
    try {
      await api.post(`/admin/executives/${view.id}/status`, { status: view.status === "active" ? "suspended" : "active", reason });
      toast.success("Account updated"); setView(null); setReason(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const resetLink = async () => {
    try { const { data: r } = await api.post(`/admin/executives/${view.id}/reset-link`); setLink(window.location.origin + r.path); toast[r.emailed ? "success" : "warning"](r.emailed ? `Reset link emailed to ${view.email}` : "Email could not be sent — share the link below manually"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Sales Executives" subtitle="Add executives yourself, or share your registration page so they can sign up free."
        actions={<>
          <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/register`); toast.success("Registration link copied"); }} data-testid="copy-register-link"><Link2 className="mr-2 h-4 w-4" />Copy sign-up link</Button>
          <AddExecutiveDialog onAdded={load} />
          <ImportExecutivesDialog onDone={load} />
        </>} />
      <Input data-testid="exec-search" placeholder="Search name, email, phone or Sales ID" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-11 sm:max-w-sm" />
      <div className="mb-4 mt-3 flex gap-2 overflow-x-auto">{[["", "Current"], ["active", "Active"], ["suspended", "Suspended"], ["deleted", "Deleted"]].map(([v, l]) => (
        <button key={l} onClick={() => { setStatus(v); setPage(1); }} data-testid={`exec-filter-${l.toLowerCase()}`}
          className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium ${status === v ? "border-[#0B192C] bg-[#0B192C] text-white" : "bg-white"}`}>{l}</button>
      ))}</div>
      {!data ? <Loading /> : data.items.length === 0 ? <EmptyState icon={Users} title="No executives found" /> : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Sales ID</TableHead><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Payable</TableHead><TableHead className="text-right">Paid</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.items.map((u) => (
                <TableRow key={u.id} data-testid={`exec-row-${u.sales_id}`}>
                  <TableCell className="whitespace-nowrap font-mono font-bold">{u.sales_id}</TableCell>
                  <TableCell className="min-w-[140px]">{u.name}<p className="text-xs text-slate-400">Joined {fmtDate(u.created_at)}</p></TableCell>
                  <TableCell className="text-sm">{u.email}<p className="text-slate-500">{u.phone}</p></TableCell>
                  <TableCell className="text-right font-mono">{u.orders} <span className="text-slate-400">({u.completed} done)</span></TableCell>
                  <TableCell className="text-right font-mono text-emerald-700">{pkr(u.payable)}</TableCell>
                  <TableCell className="text-right font-mono">{pkr(u.paid)}</TableCell>
                  <TableCell><StatusBadge map={ACC} value={u.status} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right space-x-2">
                    <Button size="sm" variant="outline" asChild><Link to={`/admin/orders?sales_id=${u.sales_id}`}>Orders</Link></Button>
                    {u.status !== "deleted" && <>
                      <Button size="sm" variant="outline" onClick={() => { setView(u); setLink(""); }} data-testid={`exec-manage-${u.sales_id}`}>Manage</Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => { setDel(u); setDelReason(""); }} data-testid={`exec-delete-${u.sales_id}`} aria-label="Delete executive"><Trash2 className="h-4 w-4" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-4"><Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} /></div>
        </Card>
      )}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{view?.name} · <span className="font-mono">{view?.sales_id}</span></DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="font-semibold">Payment details</p>
                <p>{PAY_METHODS[view.payment_details?.method] || "Not provided"} · {view.payment_details?.bank_name}</p>
                <p>{view.payment_details?.account_title} <span className="font-mono">{view.payment_details?.account_number}</span></p>
              </div>
              <div>
                <Button variant="outline" onClick={resetLink} data-testid="exec-reset-link-btn">Generate &amp; email password reset link</Button>
                {link && <div className="mt-2 break-all rounded-lg border bg-white p-2 font-mono text-xs" data-testid="exec-reset-link">{link}<p className="mt-1 font-sans text-slate-500">Share with the executive privately. Valid for 1 hour.</p></div>}
              </div>
              <Field label={`Reason to ${view.status === "active" ? "suspend" : "reactivate"} account`}><Input data-testid="exec-status-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
            </div>
          )}
          <DialogFooter><Button variant={view?.status === "active" ? "destructive" : "default"} disabled={reason.length < 3} onClick={toggle} data-testid="exec-status-toggle">{view?.status === "active" ? "Suspend account" : "Reactivate account"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {del?.name} ({del?.sales_id})?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>They will no longer be able to log in, and they will be removed from your executives list.</p>
                {del?.orders > 0
                  ? <p className="rounded-md bg-amber-50 p-2 text-amber-800">This executive has {del.orders} order(s). Their orders, commissions and payments are kept for your financial records, and the Sales ID {del.sales_id} is never reused. You can still find them under the "Deleted" filter.</p>
                  : <p>They have no orders, so the account will be removed permanently.</p>}
                {del?.payable > 0 && <p className="rounded-md bg-red-50 p-2 text-red-700">They have approved commissions waiting for payment. Pay or reject those first.</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Field label="Reason (required)"><Input data-testid="exec-delete-reason" value={delReason} onChange={(e) => setDelReason(e.target.value)} /></Field>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="exec-delete-cancel">Cancel</AlertDialogCancel>
            <Button variant="destructive" disabled={delReason.trim().length < 3} onClick={doDelete} data-testid="exec-delete-confirm">Delete executive</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
